// PasswordChangeModalGlobal.jsx — 글로벌 비밀번호 변경 모달 (Step 8 신규, 2026-04-27)
//
// 목적: AdminLayout 안 글로벌 렌더로 모든 /my/*, /admin/* 페이지에서 동작
//   - me.mustChangePassword=true 직원: 자동 모달 열림 + X/취소 버튼 비활성 (강제 변경)
//   - 사이드바 푸터 비밀번호 변경 버튼 클릭: 수동 열림 + X로 닫기 가능 (강제 X)
//
// 데이터 출처: useAppPermissions의 me 사용 (mustChangePassword 자동 검사)
// API: PUT /api/me/password (apiClient 직접 호출)
//
// Props:
//   me            — 사용자 객체 (mustChangePassword 검사용)
//   manualOpen    — 수동 열림 여부 (사이드바 클릭 등)
//   onClose       — 수동 닫기 콜백
import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { apiClient } from '../api/apiClient';
import { useToast } from './Toast';

export function PasswordChangeModalGlobal({ me, manualOpen, onClose }) {
  const { showToast } = useToast();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [autoOpen, setAutoOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // me.mustChangePassword 변경 시 자동 모달 열기 (강제 변경)
  useEffect(() => {
    if (me?.mustChangePassword) setAutoOpen(true);
  }, [me?.mustChangePassword]);

  const isOpen = autoOpen || Boolean(manualOpen);
  const isForced = Boolean(me?.mustChangePassword); // 강제 변경 시 X/취소 비활성

  if (!isOpen) return null;

  function handleClose() {
    if (isForced) return; // 강제 변경 중엔 닫기 차단
    setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setAutoOpen(false);
    if (onClose) onClose();
  }

  async function handleSubmit() {
    if (!form.currentPassword || !form.newPassword) {
      showToast('현재 비밀번호와 새 비밀번호를 입력해주세요.', 'error'); return;
    }
    if (form.newPassword !== form.confirmPassword) {
      showToast('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.', 'error'); return;
    }
    if (form.newPassword.length < 6) {
      showToast('새 비밀번호는 6자 이상이어야 합니다.', 'error'); return;
    }
    setSubmitting(true);
    try {
      const result = await apiClient.put('/api/me/password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      if (!result.ok) { showToast(result.data?.message || '비밀번호 변경에 실패했습니다.', 'error'); return; }
      showToast('비밀번호가 변경되었습니다.', 'success');
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setAutoOpen(false);
      if (onClose) onClose();
    } finally { setSubmitting(false); }
  }

  return (
    <Modal
      open={isOpen}
      title="비밀번호 변경"
      onClose={handleClose}
      footer={(
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {!isForced && (
            <button onClick={handleClose} style={btnStyle}>취소</button>
          )}
          <button onClick={handleSubmit} disabled={submitting} style={primaryBtnStyle}>
            {submitting ? '변경 중...' : '변경'}
          </button>
        </div>
      )}
    >
      <div style={{ display: 'grid', gap: 10 }}>
        {isForced && (
          <div style={warningStyle}>
            보안을 위해 비밀번호를 변경해야 합니다. 변경 전엔 다른 작업을 할 수 없습니다.
          </div>
        )}
        <div style={fieldStyle}>
          <label style={labelStyle}>현재 비밀번호</label>
          <input type="password" style={inputStyle} value={form.currentPassword}
            onChange={(e) => setForm((p) => ({ ...p, currentPassword: e.target.value }))} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>새 비밀번호 (6자 이상)</label>
          <input type="password" style={inputStyle} value={form.newPassword}
            onChange={(e) => setForm((p) => ({ ...p, newPassword: e.target.value }))} />
        </div>
        <div style={fieldStyle}>
          <label style={labelStyle}>새 비밀번호 확인</label>
          <input type="password" style={inputStyle} value={form.confirmPassword}
            onChange={(e) => setForm((p) => ({ ...p, confirmPassword: e.target.value }))} />
        </div>
      </div>
    </Modal>
  );
}

const fieldStyle = { display: 'grid', gap: 4 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' };
const inputStyle = { border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px', width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box', fontSize: 14, background: 'var(--bg-card)', color: 'var(--text)' };
const btnStyle = { border: '1px solid var(--border)', borderRadius: 8, padding: '8px 14px', background: 'var(--bg-card)', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: 'var(--text-sub)' };
const primaryBtnStyle = { ...btnStyle, background: '#1d4ed8', borderColor: '#1d4ed8', color: '#fff' };
const warningStyle = { padding: '10px 12px', borderRadius: 8, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', color: '#92400e', fontSize: 13, fontWeight: 600 };
